import React, { useEffect, useState } from "react";
import Spinner from "@src/components/atoms/spinner/spinner";
import { DragEndEvent } from "@dnd-kit/react";
import { MainContainer } from "@src/components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@src/components/templates/layouts/round-border-component-container/round-border-component-container";
import styles from "./things-os-homepage.module.scss";
import { ContainerPanel } from "@src/components/organisms/container-panel/container-panel";
import { useCreateAppsCards } from "./use-create-apps-cards";
import { HomepageCard } from "@src/components/molecules/homepage-card/homepage-card";
import { useI18N } from "@src/language/language-context";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { HomeScreenContainer } from "@src/components/organisms/home-screen-container/home-screen-container";
import HomePageLogo from "@src/assets/svg/default-homepage-logo.svg";
import { ThingsHub } from "@src/components/organisms/things-os-hub/things-hub";

const cssClassName = "things-os-homepage";
const STORAGE_KEY = "homepage-cards-order";

const arrayMove = <T,>(arr: T[], from: number, to: number): T[] => {
  const result = [...arr];
  result.splice(to, 0, result.splice(from, 1)[0]);
  return result;
};

const getImageSize = () => {
  const width = window.innerWidth;
  const dpr = window.devicePixelRatio || 1;

  if (dpr >= 2) return "x-large";

  if (width <= 1600) return "small";
  if (width <= 1720) return "medium";
  if (width <= 1920) return "large";
  return "x-large";
};

export const ThingsOSHomepage = () => {
  const i18n = useI18N();
  const namespace = useSelector(
    (state: RootState) => state.yourSlice.authContext?.customerNamespace,
  );

  const texts = {
    companyLogoAltText: i18n("pages.thingsOsHomepage.companyLogoAltText"),
  };

  const [isBgLoading, setIsBgLoading] = useState(true);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [backgroundType, setBackgroundType] = useState<
    "wallpaper" | "logo" | "default" | null
  >(null);
  const [currentSize, setCurrentSize] = useState(getImageSize());

  // Always fresh translations + permission-aware cards.
  // `permissionsLoaded` is false until the auth context has hydrated in Redux.
  const { cards: defaultCards, permissionsLoaded } = useCreateAppsCards();

  // Only track card order in state; start empty and seed once permissions arrive.
  const [cardOrder, setCardOrder] = useState<string[]>([]);

  // The visible cards can grow after mount: the dashboards feature flag
  // arrives from an async fetch that lands later. So the order is re-seeded
  // whenever the visible ids change. Kept as a joined string because
  // `defaultCards` is a new array on every render, and an array in the
  // dependencies would re-run the effect forever.
  const visibleCardIdsKey = defaultCards.map((c) => c.id).join(",");

  // Seed (or re-seed) the order as soon as we know the real card list.
  useEffect(() => {
    if (!permissionsLoaded) return;

    const defaultIds = visibleCardIdsKey ? visibleCardIdsKey.split(",") : [];

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed: string[] = JSON.parse(saved);
        const defaultIdSet = new Set(defaultIds);
        const filtered = parsed.filter((id) => defaultIdSet.has(id));
        const filteredSet = new Set(filtered);
        setCardOrder([
          ...filtered,
          ...defaultIds.filter((id) => !filteredSet.has(id)),
        ]);
        return;
      }
    } catch {
      // Ignore malformed localStorage data.
    }

    setCardOrder(defaultIds);
  }, [permissionsLoaded, visibleCardIdsKey]);

  // Derive the ordered card objects from the current order + fresh card data.
  const cardMap = Object.fromEntries(defaultCards.map((c) => [c.id, c]));
  const cards = cardOrder
    .map((id) => cardMap[id])
    .filter(Boolean) as HomepageCard[];

  const handleDragEnd = (event: DragEndEvent) => {
    const source = event.operation.source as any;
    const initialIndex = source?.initialIndex;
    const newIndex = source?.index;

    if (
      initialIndex === undefined ||
      newIndex === undefined ||
      initialIndex === newIndex
    )
      return;

    setCardOrder((prev) => {
      const newOrder = arrayMove(prev, initialIndex, newIndex);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder));
      return newOrder;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const newSize = getImageSize();
      if (newSize !== currentSize) {
        setCurrentSize(newSize);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [currentSize]);

  // Load background image once namespace is available.
  useEffect(() => {
    if (!namespace) return;

    const size = getImageSize();

    const wallpaperUrl = `https://d1fmfcun1uqqxl.cloudfront.net/ns/${namespace}/asset/${size}.jpg`;
    const wallpaperLogoUrl = `https://d1fmfcun1uqqxl.cloudfront.net/ns/${namespace}/asset/customerbg.svg`;

    const img = new Image();

    const loadLogoSvg = () => {
      const logoImg = new Image();

      logoImg.onload = () => {
        setBackgroundUrl(wallpaperLogoUrl);
        setBackgroundType("logo");
        setIsBgLoading(false);
      };

      logoImg.onerror = () => {
        setBackgroundUrl(HomePageLogo);
        setBackgroundType("default");
        setIsBgLoading(false);
      };

      logoImg.src = wallpaperLogoUrl;
    };

    img.onload = () => {
      setBackgroundUrl(wallpaperUrl);
      setBackgroundType("wallpaper");
      setIsBgLoading(false);
    };

    img.onerror = () => {
      // fallback to logo
      loadLogoSvg();
    };

    img.src = wallpaperUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [namespace, currentSize]);

  // Show spinner until both the background probe and permissions are ready.
  const isLoading = isBgLoading || !permissionsLoaded;

  return (
    <MainContainer>
      <RoundBorderComponentContainer>
        <div className={styles[cssClassName]}>
          <div className={styles[`${cssClassName}__inner-container`]}>
            {isLoading ? (
              <Spinner />
            ) : (
              <ContainerPanel>
                <HomeScreenContainer
                  backgroundUrl={
                    backgroundType === "wallpaper" ? backgroundUrl : null
                  }
                >
                  {/* Only show image when NOT wallpaper */}
                  {backgroundType !== "wallpaper" && (
                    <img
                      src={backgroundUrl || HomePageLogo}
                      className={styles[`${cssClassName}__background-image`]}
                      alt={texts.companyLogoAltText}
                    />
                  )}
                  <ThingsHub cards={cards} handleDragEnd={handleDragEnd} />
                </HomeScreenContainer>
              </ContainerPanel>
            )}
          </div>
        </div>
      </RoundBorderComponentContainer>
    </MainContainer>
  );
};
